/**
 * ADMIN — Dossiers de réclamation d'assurance (DOJO : blessures & accidents).
 * Ouverture par admin OU membre. Suivi interne + génération PDF prêt pour l'assureur.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Shield, Plus, Trash, FloppyDisk, CircleNotch, FilePdf, Paperclip,
  X, UploadSimple, User, PencilSimple, PaperPlaneTilt, DownloadSimple, ChartBar,
} from "@phosphor-icons/react";

const EMPTY = {
  incident_type: "blessure_entrainement", incident_date: "", incident_time: "",
  location: "Institut Impact Élite Martial, Val-d'Or, QC", discipline: "", description: "",
  injured: { name: "", dob: "", phone: "", email: "", address: "" },
  body_part: "", injury_description: "", coach_name: "", waiver_signed: false,
  witnesses: [], medical: { consulted: false, provider: "", report_notes: "" },
  insurer_name: "", insurer_email: "", policy_number: "", notes: "", member_email: "",
};

const STATUS_STYLE = {
  ouvert: "bg-amber-500/15 text-amber-400",
  soumis: "bg-blue-500/15 text-blue-400",
  regle: "bg-green-500/15 text-green-400",
  refuse: "bg-red-500/15 text-red-400",
};

export default function InsuranceClaimsModule() {
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // claim or EMPTY (create)
  const [detail, setDetail] = useState(null);    // claim id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/insurance-claims", { params: { status: filter || undefined } });
      setClaims(r.data.claims);
      setStats(r.data.stats);
      setStatuses(r.data.statuses);
      setIncidentTypes(r.data.incident_types);
    } catch (e) { toast.error("Chargement des dossiers impossible."); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm("Supprimer définitivement ce dossier ?")) return;
    try { await api.delete(`/insurance-claims/${id}`); toast.success("Dossier supprimé."); load(); }
    catch (e) { toast.error("Suppression impossible."); }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="insurance-claims-module">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <Shield size={26} weight="fill" className="text-[#C9A961]" />
          <h1 className="text-2xl font-bold text-white">Réclamations d'assurance</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-claim-btn"
          className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold text-sm px-4 py-2.5 hover:opacity-90">
          <Plus size={16} /> Nouveau dossier
        </button>
      </div>
      <p className="text-white/50 text-sm mb-6">Blessures pendant l'entraînement et accidents au dojo. Suivi interne + PDF prêt pour l'assureur.</p>

      {/* Export rapport mensuel */}
      <ExportBar />

      {/* Tableau de bord sécurité */}
      <SafetyDashboard />

      {/* KPI + filtres */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <FilterKpi label="Tous" value={stats.total || 0} active={filter === ""} onClick={() => setFilter("")} testid="filter-all" />
        {statuses.map((s) => (
          <FilterKpi key={s.value} label={s.label} value={stats[s.value] || 0}
            active={filter === s.value} onClick={() => setFilter(s.value)} testid={`filter-${s.value}`} />
        ))}
      </div>

      {loading ? (
        <CircleNotch size={28} className="animate-spin text-[#C9A961]" />
      ) : claims.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 bg-white/[0.02] p-6 text-center" data-testid="no-claims">
          Aucun dossier{filter ? " pour ce statut" : ""}. Clique « Nouveau dossier » pour en ouvrir un.
        </p>
      ) : (
        <div className="space-y-2" data-testid="claims-list">
          {claims.map((c) => (
            <div key={c.id} className="flex items-center gap-3 border border-white/8 bg-white/[0.02] px-4 py-3" data-testid={`claim-row-${c.id}`}>
              <span className="kz-mono text-[11px] text-[#C9A961] w-24 shrink-0">{c.claim_number}</span>
              <div className="flex-1 min-w-0">
                <div className="text-white/90 text-sm truncate">{c.injured?.name || "—"} · {c.incident_label}</div>
                <div className="text-white/40 text-xs">{c.incident_date || "date ?"} · déposé par {c.filed_by === "member" ? "membre" : "admin"}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 shrink-0 ${STATUS_STYLE[c.status] || ""}`} data-testid={`claim-status-${c.id}`}>{c.status_label}</span>
              <button onClick={() => setDetail(c.id)} data-testid={`claim-open-${c.id}`} className="text-white/60 hover:text-white shrink-0" title="Ouvrir"><PencilSimple size={16} /></button>
              <button onClick={() => remove(c.id)} data-testid={`claim-delete-${c.id}`} className="text-white/40 hover:text-red-400 shrink-0" title="Supprimer"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ClaimForm claim={editing} incidentTypes={incidentTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {detail && (
        <ClaimDetail claimId={detail} statuses={statuses}
          onClose={() => setDetail(null)}
          onEdit={(c) => { setDetail(null); setEditing(c); }}
          onChanged={load} />
      )}
    </div>
  );
}

function ExportBar() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);

  async function download(allMonths) {
    setBusy(true);
    try {
      const params = allMonths ? {} : { month };
      const r = await api.get("/insurance-claims/export/report", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `rapport-reclamations-${allMonths ? "complet" : month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible."); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex flex-wrap items-end gap-3 border border-white/10 bg-white/[0.02] p-4 mb-6" data-testid="export-bar">
      <div>
        <label className="block text-white/50 text-xs mb-1">Rapport du mois</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="export-month"
          className="bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
      </div>
      <button onClick={() => download(false)} disabled={busy} data-testid="export-month-btn"
        className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50">
        {busy ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={15} />} Exporter le mois (PDF)
      </button>
      <button onClick={() => download(true)} disabled={busy} data-testid="export-all-btn"
        className="inline-flex items-center gap-2 border border-white/15 text-white/80 text-sm px-4 py-2 hover:bg-white/[0.05] disabled:opacity-50">
        <FilePdf size={15} /> Tout exporter
      </button>
    </div>
  );
}

function SafetyDashboard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/insurance-claims/stats/safety").then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const Bars = ({ title, rows, accent }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return (
      <div>
        <p className="text-white/60 text-xs font-semibold mb-2">{title}</p>
        {rows.length === 0 ? <p className="text-white/30 text-xs">Aucune donnée.</p> : (
          <div className="space-y-1.5">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center gap-2" data-testid={`safety-bar-${r.label}`}>
                <span className="text-white/70 text-xs w-28 truncate shrink-0">{r.label}</span>
                <div className="flex-1 bg-white/5 h-4 relative">
                  <div className="h-4" style={{ width: `${(r.count / max) * 100}%`, background: accent }} />
                </div>
                <span className="text-white/80 text-xs w-6 text-right shrink-0">{r.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="border border-white/10 bg-white/[0.02] mb-6" data-testid="safety-dashboard">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="safety-toggle">
        <span className="text-white font-semibold flex items-center gap-2"><ChartBar size={18} className="text-[#C9A961]" /> Tableau de bord sécurité <span className="text-white/40 text-xs">({d.total} incident{d.total > 1 ? "s" : ""})</span></span>
        <span className="text-white/40 text-xs">{open ? "Masquer ▲" : "Afficher ▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Bars title="📅 Par mois (12 derniers)" rows={d.by_month} accent="#C9A961" />
          <Bars title="🥋 Par discipline" rows={d.by_discipline} accent="#D90429" />
          <Bars title="🩹 Parties du corps (top 10)" rows={d.by_body_part} accent="#3b82f6" />
          <Bars title="⚠️ Par type d'incident" rows={d.by_type} accent="#10b981" />
        </div>
      )}
    </div>
  );
}

function FilterKpi({ label, value, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`border p-3 text-left transition-colors ${active ? "border-[#C9A961] bg-[#C9A961]/[0.08]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-white/50 text-xs">{label}</div>
    </button>
  );
}

function ClaimForm({ claim, incidentTypes, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...claim, injured: { ...EMPTY.injured, ...(claim.injured || {}) }, medical: { ...EMPTY.medical, ...(claim.medical || {}) }, witnesses: claim.witnesses || [] });
  const [busy, setBusy] = useState(false);
  const isEdit = !!claim.id;
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upInj = (k, v) => setF((p) => ({ ...p, injured: { ...p.injured, [k]: v } }));
  const upMed = (k, v) => setF((p) => ({ ...p, medical: { ...p.medical, [k]: v } }));

  async function save() {
    if (!f.description.trim()) { toast.error("La description de l'incident est requise."); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/insurance-claims/${claim.id}`, f);
      else await api.post("/insurance-claims", f);
      toast.success(isEdit ? "Dossier mis à jour." : "Dossier créé.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible."); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/75 flex items-start justify-center overflow-y-auto p-4" data-testid="claim-form">
      <div className="w-full max-w-2xl bg-[#0C0A09] border border-white/15 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">{isEdit ? `Dossier ${claim.claim_number}` : "Nouveau dossier de réclamation"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <Sel label="Type d'incident" value={f.incident_type} onChange={(v) => up("incident_type", v)} testid="cf-type"
            opts={incidentTypes.map((t) => ({ v: t.value, l: t.label }))} />
          <Inp label="Discipline" value={f.discipline} onChange={(v) => up("discipline", v)} testid="cf-discipline" />
          <Inp label="Date de l'incident" value={f.incident_date} onChange={(v) => up("incident_date", v)} testid="cf-date" type="date" />
          <Inp label="Heure" value={f.incident_time} onChange={(v) => up("incident_time", v)} testid="cf-time" type="time" />
        </div>
        <Inp label="Lieu" value={f.location} onChange={(v) => up("location", v)} testid="cf-location" />
        <Inp label="Coach / superviseur présent" value={f.coach_name} onChange={(v) => up("coach_name", v)} testid="cf-coach" />
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer my-2 p-2 border border-[#C9A961]/30 bg-[#C9A961]/[0.05]" data-testid="cf-waiver-label">
          <input type="checkbox" checked={!!f.waiver_signed} onChange={(e) => up("waiver_signed", e.target.checked)} data-testid="cf-waiver" />
          🛡️ Décharge de responsabilité signée par l'élève / le parent
        </label>
        <Txt label="Description de l'incident *" value={f.description} onChange={(v) => up("description", v)} testid="cf-description" />

        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2 flex items-center gap-2"><User size={15} /> Personne blessée / impliquée</h4>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Nom complet" value={f.injured.name} onChange={(v) => upInj("name", v)} testid="cf-inj-name" />
          <Inp label="Date de naissance" value={f.injured.dob} onChange={(v) => upInj("dob", v)} testid="cf-inj-dob" type="date" />
          <Inp label="Téléphone" value={f.injured.phone} onChange={(v) => upInj("phone", v)} testid="cf-inj-phone" />
          <Inp label="Courriel" value={f.injured.email} onChange={(v) => upInj("email", v)} testid="cf-inj-email" />
        </div>
        <Inp label="Adresse" value={f.injured.address} onChange={(v) => upInj("address", v)} testid="cf-inj-address" />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Partie du corps atteinte" value={f.body_part} onChange={(v) => up("body_part", v)} testid="cf-bodypart" />
        </div>
        <Txt label="Nature de la blessure" value={f.injury_description} onChange={(v) => up("injury_description", v)} testid="cf-injury" />

        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2">Soins médicaux</h4>
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer mb-2" data-testid="cf-med-consulted-label">
          <input type="checkbox" checked={f.medical.consulted} onChange={(e) => upMed("consulted", e.target.checked)} data-testid="cf-med-consulted" />
          Consultation médicale effectuée
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Établissement / médecin" value={f.medical.provider} onChange={(v) => upMed("provider", v)} testid="cf-med-provider" />
        </div>
        <Txt label="Notes médicales" value={f.medical.report_notes} onChange={(v) => upMed("report_notes", v)} testid="cf-med-notes" />

        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2">Témoins</h4>
        {f.witnesses.map((w, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <input value={w.name} onChange={(e) => setF((p) => ({ ...p, witnesses: p.witnesses.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))}
              placeholder="Nom du témoin" data-testid={`cf-witness-name-${i}`} className="flex-1 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
            <input value={w.phone} onChange={(e) => setF((p) => ({ ...p, witnesses: p.witnesses.map((x, j) => j === i ? { ...x, phone: e.target.value } : x) }))}
              placeholder="Téléphone" data-testid={`cf-witness-phone-${i}`} className="w-40 bg-black/40 border border-white/15 text-white text-sm px-2 py-1.5" />
            <button onClick={() => setF((p) => ({ ...p, witnesses: p.witnesses.filter((_, j) => j !== i) }))} className="text-white/40 hover:text-red-400"><Trash size={15} /></button>
          </div>
        ))}
        <button onClick={() => setF((p) => ({ ...p, witnesses: [...p.witnesses, { name: "", phone: "" }] }))} data-testid="cf-add-witness"
          className="text-[#C9A961] text-xs hover:underline mb-2">+ Ajouter un témoin</button>

        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2">Assureur (optionnel)</h4>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Assureur" value={f.insurer_name} onChange={(v) => up("insurer_name", v)} testid="cf-insurer" />
          <Inp label="N° de police" value={f.policy_number} onChange={(v) => up("policy_number", v)} testid="cf-policy" />
        </div>
        <Inp label="Courriel de l'assureur" value={f.insurer_email} onChange={(v) => up("insurer_email", v)} testid="cf-insurer-email" type="email" />
        <Inp label="Courriel du membre lié (optionnel)" value={f.member_email} onChange={(v) => up("member_email", v)} testid="cf-member-email" />
        <Txt label="Notes internes" value={f.notes} onChange={(v) => up("notes", v)} testid="cf-notes" />

        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy} data-testid="cf-save"
            className="flex-1 bg-[#C9A961] text-black font-semibold py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <><FloppyDisk size={15} /> {isEdit ? "Enregistrer" : "Créer le dossier"}</>}
          </button>
          <button onClick={onClose} className="border border-white/15 text-white/70 px-4 py-2.5">Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ClaimDetail({ claimId, statuses, onClose, onEdit, onChanged }) {
  const [c, setC] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [insurerEmail, setInsurerEmail] = useState("");
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/insurance-claims/${claimId}`); setC(r.data.claim); }
    catch (e) { toast.error("Chargement du dossier impossible."); onClose(); }
  }, [claimId, onClose]);
  useEffect(() => { load(); }, [load]);

  async function changeStatus(status) {
    try { await api.patch(`/insurance-claims/${claimId}/status`, { status }); toast.success("Statut mis à jour."); load(); onChanged(); }
    catch (e) { toast.error("Changement de statut impossible."); }
  }

  async function upload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "Pièce jointe");
      await api.post(`/insurance-claims/${claimId}/upload`, fd);
      toast.success("Pièce jointe ajoutée."); load();
    } catch (er) { toast.error("Upload impossible."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function removeAtt(attId) {
    try { await api.delete(`/insurance-claims/${claimId}/attachments/${attId}`); load(); }
    catch (er) { toast.error("Suppression impossible."); }
  }

  async function downloadPdf() {
    try {
      const r = await api.get(`/insurance-claims/${claimId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reclamation-${c?.claim_number || claimId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (er) { toast.error("Génération du PDF impossible."); }
  }

  function openSubmit() {
    setInsurerEmail(c?.insurer_email || "");
    setSubmitMsg("");
    setSubmitOpen(true);
  }

  async function submitToInsurer() {
    if (!insurerEmail.trim()) { toast.error("Courriel de l'assureur requis."); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/insurance-claims/${claimId}/submit-to-insurer`,
        { to_email: insurerEmail.trim(), message: submitMsg.trim() });
      toast.success(`Envoyé à ${r.data.to} (${r.data.attachments} pièce(s)). Statut → soumis.`);
      setSubmitOpen(false); load(); onChanged();
    } catch (er) { toast.error(er?.response?.data?.detail || "Envoi impossible."); }
    finally { setSubmitting(false); }
  }

  if (!c) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/75 flex items-start justify-center overflow-y-auto p-4" data-testid="claim-detail" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0C0A09] border border-white/15 my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-lg">{c.claim_number}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-4">{c.incident_label} · {c.incident_date || "date ?"} · {c.location}</p>

        {/* Statut */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-white/50 text-xs">Statut :</span>
          {statuses.map((s) => (
            <button key={s.value} onClick={() => changeStatus(s.value)} data-testid={`set-status-${s.value}`}
              className={`text-xs px-2.5 py-1 border ${c.status === s.value ? "border-[#C9A961] text-[#C9A961]" : "border-white/15 text-white/60 hover:bg-white/[0.05]"}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm mb-4">
          <Row k="Blessé(e)" v={c.injured?.name} />
          <Row k="Téléphone" v={c.injured?.phone} />
          <Row k="Partie du corps" v={c.body_part} />
          <Row k="Discipline" v={c.discipline} />
          <Row k="Coach" v={c.coach_name} />
          <Row k="Décharge signée" v={c.waiver_signed ? "✅ Oui" : "❌ Non"} />
          <Row k="Déposé par" v={c.filed_by === "member" ? "Membre" : "Admin"} />
        </div>
        <div className="mb-3">
          <div className="text-white/40 text-xs mb-1">Description</div>
          <div className="text-white/80 text-sm whitespace-pre-wrap">{c.description || "—"}</div>
        </div>
        {c.injury_description && (
          <div className="mb-3">
            <div className="text-white/40 text-xs mb-1">Nature de la blessure</div>
            <div className="text-white/80 text-sm whitespace-pre-wrap">{c.injury_description}</div>
          </div>
        )}

        {/* Pièces jointes */}
        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2 flex items-center gap-2"><Paperclip size={15} /> Pièces jointes</h4>
        <div className="space-y-1.5 mb-3" data-testid="attachments-list">
          {(c.attachments || []).length === 0 && <p className="text-white/40 text-xs">Aucune pièce jointe.</p>}
          {(c.attachments || []).map((a) => (
            <div key={a.id} className="flex items-center gap-2 border border-white/8 px-3 py-1.5 text-xs">
              <a href={a.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-white/80 hover:text-[#C9A961]">{a.filename || a.url}</a>
              <span className="text-white/40">{a.date}</span>
              <button onClick={() => removeAtt(a.id)} className="text-white/40 hover:text-red-400"><Trash size={14} /></button>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 border border-white/15 text-white/80 text-sm px-3 py-2 hover:bg-white/[0.05] cursor-pointer" data-testid="upload-attachment">
          {uploading ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} />} Ajouter une pièce (rapport médical, photo…)
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>

        <div className="flex gap-2 mt-5 pt-4 border-t border-white/8 flex-wrap">
          <button onClick={downloadPdf} data-testid="download-pdf"
            className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold text-sm px-4 py-2.5 hover:opacity-90">
            <FilePdf size={16} /> Télécharger le PDF (assureur)
          </button>
          <button onClick={openSubmit} data-testid="submit-insurer-btn"
            className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2.5 hover:opacity-90">
            <PaperPlaneTilt size={15} /> Soumettre à l'assureur
          </button>
          <button onClick={() => onEdit(c)} data-testid="edit-claim"
            className="inline-flex items-center gap-2 border border-[#C9A961]/50 text-[#C9A961] text-sm px-4 py-2.5 hover:bg-[#C9A961]/10">
            <PencilSimple size={15} /> Modifier
          </button>
        </div>

        {submitOpen && (
          <div className="mt-4 border border-[#C9A961]/40 bg-[#C9A961]/[0.05] p-4" data-testid="submit-insurer-dialog">
            <p className="text-white/70 text-sm mb-2">Le PDF du dossier et les pièces jointes seront envoyés par courriel à l'assureur.</p>
            <label className="block text-white/50 text-xs mb-1">Courriel de l'assureur *</label>
            <input type="email" value={insurerEmail} onChange={(e) => setInsurerEmail(e.target.value)} data-testid="submit-insurer-email"
              placeholder="reclamations@assureur.com" className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2 mb-2" />
            <label className="block text-white/50 text-xs mb-1">Message (optionnel)</label>
            <textarea value={submitMsg} onChange={(e) => setSubmitMsg(e.target.value)} data-testid="submit-insurer-message" rows={2}
              className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2 mb-3 resize-y" />
            <div className="flex gap-2">
              <button onClick={submitToInsurer} disabled={submitting} data-testid="submit-insurer-send"
                className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50">
                {submitting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />} Envoyer
              </button>
              <button onClick={() => setSubmitOpen(false)} className="border border-white/15 text-white/70 text-sm px-4 py-2">Annuler</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div><span className="text-white/40 text-xs">{k} : </span><span className="text-white/80">{v || "—"}</span></div>;
}

function Inp({ label, value, onChange, testid, type = "text" }) {
  return (
    <div className="mb-2">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2" />
    </div>
  );
}

function Txt({ label, value, onChange, testid }) {
  return (
    <div className="mb-2">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={3}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2 resize-y" />
    </div>
  );
}

function Sel({ label, value, onChange, opts, testid }) {
  return (
    <div>
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-2 py-2">
        {opts.map((o) => <option key={o.v} value={o.v} className="bg-[#0C0A09]">{o.l}</option>)}
      </select>
    </div>
  );
}
